import Dexie, { Table } from 'dexie';

// Only log in development mode
const isDev = import.meta.env.DEV;
const log = (...args: unknown[]) => isDev && console.log(...args);

export type SyncAction = 'create' | 'update' | 'delete';
// 'failed' is a terminal dead-letter state: the item exhausted its retries and is
// no longer counted as pending or auto-retried. It can be retried or discarded
// explicitly by the user from the Status dialog.
export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'error' | 'failed';

export interface SyncQueueItem {
  id: string;
  action: SyncAction;
  table: string;
  data: string;
  status: SyncStatus;
  retries: number;
  created_at: string;
  error_message: string | null;
}

export interface CachedItem {
  id: string;
  data: string;
  synced_at: string;
}

export interface OfflineMetadata {
  key: string;
  value: string;
}

class OfflineDatabase extends Dexie {
  syncQueue!: Table<SyncQueueItem, string>;
  cachedProducts!: Table<CachedItem, string>;
  cachedCategories!: Table<CachedItem, string>;
  cachedOrders!: Table<CachedItem, string>;
  metadata!: Table<OfflineMetadata, string>;

  constructor() {
    super('blazepos-offline');

    this.version(1).stores({
      syncQueue: 'id, status, created_at',
      cachedProducts: 'id, synced_at',
      cachedCategories: 'id, synced_at',
      cachedOrders: 'id, synced_at',
      metadata: 'key',
    });
  }

  // Queue an action for sync
  async queueAction(
    action: SyncAction,
    table: string,
    data: Record<string, unknown>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    await this.syncQueue.add({
      id,
      action,
      table,
      data: JSON.stringify(data),
      status: 'pending',
      retries: 0,
      created_at: now,
      error_message: null,
    });

    log(`[Offline] Queued action: ${action} on ${table}`);
    return id;
  }

  // Get pending sync items
  async getPendingItems(): Promise<SyncQueueItem[]> {
    return this.syncQueue
      .where('status')
      .anyOf('pending', 'error')
      .sortBy('created_at');
  }

  // Get pending count
  async getPendingCount(): Promise<number> {
    return this.syncQueue
      .where('status')
      .anyOf('pending', 'error')
      .count();
  }

  // Update sync item status
  async updateSyncStatus(
    id: string,
    status: SyncStatus,
    errorMessage?: string
  ): Promise<void> {
    const current = await this.syncQueue.get(id);
    await this.syncQueue.update(id, {
      status,
      error_message: errorMessage || null,
      retries: (current?.retries ?? 0) + 1,
    });
  }

  // Move an item to the terminal 'failed' dead-letter state (no retry bump,
  // no longer counted as pending).
  async markFailed(id: string, errorMessage?: string): Promise<void> {
    await this.syncQueue.update(id, {
      status: 'failed',
      error_message: errorMessage || null,
    });
  }

  // Get items that have permanently failed to sync
  async getFailedItems(): Promise<SyncQueueItem[]> {
    return this.syncQueue.where('status').equals('failed').sortBy('created_at');
  }

  // Count of permanently failed items
  async getFailedCount(): Promise<number> {
    return this.syncQueue.where('status').equals('failed').count();
  }

  // Requeue all failed items for another sync attempt
  async retryFailedItems(): Promise<void> {
    await this.syncQueue
      .where('status')
      .equals('failed')
      .modify({ status: 'pending', retries: 0, error_message: null });
  }

  // Permanently discard all failed items
  async discardFailedItems(): Promise<void> {
    await this.syncQueue.where('status').equals('failed').delete();
  }

  // Remove synced item
  async removeSyncItem(id: string): Promise<void> {
    await this.syncQueue.delete(id);
  }

  // Cache products
  async cacheProducts(items: Array<{ id: string; [key: string]: unknown }>): Promise<void> {
    const now = new Date().toISOString();
    await this.cachedProducts.clear();
    await this.cachedProducts.bulkAdd(
      items.map((item) => ({
        id: item.id,
        data: JSON.stringify(item),
        synced_at: now,
      }))
    );
    log(`[Offline] Cached ${items.length} products`);
  }

  // Cache categories
  async cacheCategories(items: Array<{ id: string; [key: string]: unknown }>): Promise<void> {
    const now = new Date().toISOString();
    await this.cachedCategories.clear();
    await this.cachedCategories.bulkAdd(
      items.map((item) => ({
        id: item.id,
        data: JSON.stringify(item),
        synced_at: now,
      }))
    );
    log(`[Offline] Cached ${items.length} categories`);
  }

  // Cache orders
  async cacheOrders(items: Array<{ id: string; [key: string]: unknown }>): Promise<void> {
    const now = new Date().toISOString();
    await this.cachedOrders.clear();
    await this.cachedOrders.bulkAdd(
      items.map((item) => ({
        id: item.id,
        data: JSON.stringify(item),
        synced_at: now,
      }))
    );
    log(`[Offline] Cached ${items.length} orders`);
  }

  // Get cached products
  async getCachedProducts<T>(): Promise<T[]> {
    const items = await this.cachedProducts.toArray();
    return items.map((item) => JSON.parse(item.data) as T);
  }

  // Get cached categories
  async getCachedCategories<T>(): Promise<T[]> {
    const items = await this.cachedCategories.toArray();
    return items.map((item) => JSON.parse(item.data) as T);
  }

  // Get cached orders
  async getCachedOrders<T>(): Promise<T[]> {
    const items = await this.cachedOrders.toArray();
    return items.map((item) => JSON.parse(item.data) as T);
  }

  // Clear all cache
  async clearCache(): Promise<void> {
    await Promise.all([
      this.cachedProducts.clear(),
      this.cachedCategories.clear(),
      this.cachedOrders.clear(),
    ]);
    log('[Offline] Cache cleared');
  }

  // Clear sync queue
  async clearSyncQueue(): Promise<void> {
    await this.syncQueue.clear();
    log('[Offline] Sync queue cleared');
  }

  // Get metadata
  async getMetadata(key: string): Promise<string | null> {
    const item = await this.metadata.get(key);
    return item?.value ?? null;
  }

  // Set metadata
  async setMetadata(key: string, value: string): Promise<void> {
    await this.metadata.put({ key, value });
  }

  // Get last sync time
  async getLastSyncAt(): Promise<Date | null> {
    const value = await this.getMetadata('lastSyncAt');
    return value ? new Date(value) : null;
  }

  // Set last sync time
  async setLastSyncAt(date: Date): Promise<void> {
    await this.setMetadata('lastSyncAt', date.toISOString());
  }

  // Get storage usage estimate
  async getStorageUsage(): Promise<{ used: number; quota: number }> {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage ?? 0,
        quota: estimate.quota ?? 0,
      };
    }
    return { used: 0, quota: 0 };
  }
}

// Singleton instance
export const offlineDb = new OfflineDatabase();
